import type { FlightItem, FlightToIdeaPayload, TravelClass } from '../types/flight.types';

export function newFlightExternalId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `flight-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

export type FlightSearchContextForSave = {
  flightFrom: string;
  flightTo: string;
  departDate: string;
  returnDate?: string;
};

export function buildFlightToIdeaPayload(
  flight: FlightItem,
  travelersCount: number,
  travelClass: TravelClass,
  searchContext: FlightSearchContextForSave
): FlightToIdeaPayload {
  return buildFlightToIdeaPayloadFromFields(flight, travelersCount, travelClass, searchContext);
}

export function buildFlightToIdeaPayloadFromFields(
  flight: Pick<
    FlightItem,
    | 'depart_departure_date_time'
    | 'depart_arrival_date_time'
    | 'return_departure_date_time'
    | 'return_arrival_date_time'
    | 'depart_departure_airport'
    | 'depart_arrival_airport'
    | 'return_departure_airport'
    | 'return_arrival_airport'
    | 'depart_flight_number'
    | 'return_flight_number'
    | 'depart_airline'
    | 'return_airline'
    | 'depart_airline_name'
    | 'return_airline_name'
    | 'price'
  >,
  travelersCount: number,
  travelClass: TravelClass,
  searchContext: FlightSearchContextForSave
): FlightToIdeaPayload {
  const external_id = newFlightExternalId();
  const discover_payload = JSON.stringify({
    v: 1,
    source: 'discover_flight_search',
    flight_from: searchContext.flightFrom,
    flight_to: searchContext.flightTo,
    depart_date: searchContext.departDate,
    return_date: searchContext.returnDate ?? null,
    depart_departure_airport: flight.depart_departure_airport,
    depart_arrival_airport: flight.depart_arrival_airport,
    return_departure_airport: flight.return_departure_airport ?? null,
    return_arrival_airport: flight.return_arrival_airport ?? null,
    price: flight.price,
  });

  return {
    depart_departure_date_time: flight.depart_departure_date_time,
    depart_arrival_date_time: flight.depart_arrival_date_time,
    return_departure_date_time: flight.return_departure_date_time ?? '',
    return_arrival_date_time: flight.return_arrival_date_time ?? '',
    depart_departure_airport: flight.depart_departure_airport,
    depart_arrival_airport: flight.depart_arrival_airport,
    return_departure_airport: flight.return_departure_airport ?? '',
    return_arrival_airport: flight.return_arrival_airport ?? '',
    depart_flight_number: flight.depart_flight_number,
    return_flight_number: flight.return_flight_number ?? '',
    depart_airline: flight.depart_airline,
    return_airline: flight.return_airline ?? '',
    depart_airline_name: flight.depart_airline_name,
    return_airline_name: flight.return_airline_name ?? '',
    price: flight.price,
    travelers_count: travelersCount,
    travel_class: travelClass,
    external_id,
    discover_payload,
  };
}

export function tripDateRangeFromFlightPayload(
  body: FlightToIdeaPayload
): { start_date: string; end_date: string } | undefined {
  const days = [
    body.depart_departure_date_time,
    body.depart_arrival_date_time,
    body.return_departure_date_time,
    body.return_arrival_date_time,
  ]
    .map(value => value?.trim().slice(0, 10))
    .filter((value): value is string => Boolean(value));

  if (days.length === 0) return undefined;

  const sorted = [...days].sort();
  return { start_date: sorted[0], end_date: sorted[sorted.length - 1] };
}
